import { useModal } from '@billy/design-system'
import Cookies from 'js-cookie'
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router'

import { ConnectBankModal } from '@features/bankAccounts/elements/ConnectBankModal'
import { OpenBankingAutoConnectModal } from '@features/openBanking/elements/OpenBankingAutoConnectModal'
import { OpenBankingAutoConnectModalSteps } from '@features/openBanking/elements/OpenBankingAutoConnectModal/enums/openBankingAutoConnectModalSteps'
import { CreateBankConnectionModal } from '@features/shineBanking/elements/CreateBankConnectionModal'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { CookieKeys } from '../../enums/cookieKeys'
import { EmberRoute } from '../../enums/emberRoute'
import { ModalId } from '../../enums/modalId'
import { Scope } from '../../enums/scope'
import { useSegment } from '../../hooks'
import { useEmberRouteUrl } from '../../hooks/routing/useEmberRouteUrl'
import { useGetQueryFromEmberRoute } from '../../hooks/useGetQueryFromEmberRoute'
import { useOrganizationUiSetup } from '../../hooks/useOrganizationUiSetup'
import { isAuthorized } from '../../utils/isAuthorized'
import { BankWidgetContextProvider } from './contexts/bankWidgetContext'
import { DashboardSettingsContextProvider } from './contexts/dashboardSettingsContext'
import { DashboardLayout } from './elements/DashboardLayout'

export const Dashboard = () => {
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const location = useLocation()
  const { url: integrationsUrl } = useEmberRouteUrl(EmberRoute.Integrations)
  const { open: openCreateBankConnectionModal } = useModal(ModalId.CreateBankConnectionModal)
  const { open: openAutoConnectModal } = useModal(ModalId.OpenBankingAutoConnectModal)
  const { isUiSetupShineInvoicingAccounting, isUiSetupSupernavInvoicingAccounting } = useOrganizationUiSetup()

  const isShineMergeEnabled = isAuthorized(Scope.IdentityMergeWrite)
  const isShineConnectionEnabled =
    isShineMergeEnabled && (isUiSetupShineInvoicingAccounting || isUiSetupSupernavInvoicingAccounting)

  const {
    mobilepaystatus: mobilePayQuery,
    code: codeQuery,
    state: stateQuery,
    success,
    connectionId,
  } = useGetQueryFromEmberRoute({
    queryParamsValidKeys: ['mobilepaystatus', 'code', 'state', 'success', 'connectionId'],
  }) || {}

  useEffect(() => {
    if (Cookies.get(CookieKeys.FirstSignin) === 'true') {
      track('Organization Created (Client)')
      Cookies.remove(CookieKeys.FirstSignin, { domain: window.location.hostname.split('.').slice(1).join('.') })
    }

    if (!mobilePayQuery) {
      track('Dashboard Viewed (Client)')
    }
  }, [mobilePayQuery, track])

  useEffect(() => {
    if (stateQuery && isShineConnectionEnabled) {
      openCreateBankConnectionModal()
    }
  }, [openCreateBankConnectionModal, stateQuery, isShineConnectionEnabled])

  useEffect(() => {
    if (success === 'true' && connectionId) {
      openAutoConnectModal()
    } else if (success === 'false') {
      openAutoConnectModal()
    }
  }, [openAutoConnectModal, success, connectionId])

  if (mobilePayQuery && integrationsUrl) {
    return <Navigate to={integrationsUrl + location.search} replace />
  }

  const organizationId = organization?.id || ''

  return (
    <>
      <DashboardSettingsContextProvider>
        <BankWidgetContextProvider organizationId={organizationId}>
          <DashboardLayout />
        </BankWidgetContextProvider>
      </DashboardSettingsContextProvider>
      <ConnectBankModal id={ModalId.BankConnectionAddAccountModal} />
      <CreateBankConnectionModal id={ModalId.CreateBankConnectionModal} code={codeQuery} state={stateQuery} />
      <OpenBankingAutoConnectModal
        id={ModalId.OpenBankingAutoConnectModal}
        initialState={success === 'false' ? OpenBankingAutoConnectModalSteps.ConnectionFailed : undefined}
      />
    </>
  )
}
